import { toAppError } from '@repo/api-client';
import { useClient, useDeleteClient, useInvoices, useUploadClientPhoto } from '@repo/queries';
import { useActiveOrganizationId } from '@repo/api-client/react';
import { Edit3, Loader2, Mail, Phone, Plus, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/components/ui/use-toast';
import { Page, PageHeader } from '@/features/common/PageHeader';
import { describeAppError } from '@/features/common/describeAppError';
import { displayWireMoney } from '@/features/money';
import { ClientAvatar, ImageUploader, inferClientTag } from '@/features/avatar';
import { NotFoundScreen } from '@/app/public/NotFound';
import { useActiveRole } from '@/features/auth/useActiveRole';

export function ClientDetailScreen() {
  const params = useParams<{ clientId: string }>();
  const clientId = params.clientId ?? '';
  const navigate = useNavigate();
  const { toast } = useToast();
  const orgId = useActiveOrganizationId();
  const { canEditOrg } = useActiveRole();
  const { client, loading, error } = useClient(clientId);
  const { invoices } = useInvoices();
  const { deleteClient, loading: deleting } = useDeleteClient();
  const { uploadPhoto, loading: uploadingPhoto } = useUploadClientPhoto();
  const [confirmDelete, setConfirmDelete] = useState(false);

  async function onUploadPhoto(file: File) {
    if (!orgId || !client) return;
    try {
      await uploadPhoto({ organizationId: orgId, clientId: client.id, file });
      toast({ title: 'Photo updated', variant: 'success' });
    } catch (err) {
      const desc = describeAppError(err);
      toast({ title: desc.title, description: desc.message, variant: 'danger' });
      throw err;
    }
  }

  const clientInvoices = useMemo(
    () => (invoices ?? []).filter((inv) => inv.clientId === clientId),
    [invoices, clientId],
  );

  const outstanding = useMemo(() => {
    return clientInvoices.reduce(
      (sum, inv) =>
        sum +
        (inv.status === 'sent' || inv.status === 'pending' || inv.status === 'overdue'
          ? BigInt(inv.totals.amountDue)
          : 0n),
      0n,
    );
  }, [clientInvoices]);

  if (loading || !client) {
    if (error) return <NotFoundScreen />;
    return (
      <Page>
        <div className="flex justify-center py-16">
          <Loader2 className="size-6 animate-spin text-muted-foreground" />
        </div>
      </Page>
    );
  }

  const email = (client.contact?.email as string | undefined) ?? null;
  const phone = (client.contact?.phone as string | undefined) ?? null;
  const website = (client.contact?.website as string | undefined) ?? null;

  return (
    <Page>
      <PageHeader
        title={
          <span className="flex items-center gap-3">
            <ClientAvatar
              name={client.name}
              photoUrl={client.photoUrl ?? null}
              tag={inferClientTag({ name: client.name, company: client.company })}
              size={48}
            />
            <span className="truncate">{client.name}</span>
          </span>
        }
        description={client.company ?? undefined}
        breadcrumbs={
          <span>
            <Link to="/clients" className="hover:text-foreground">
              Clients
            </Link>{' '}
            / {client.name}
          </span>
        }
        actions={
          canEditOrg ? (
            <>
              <Button asChild variant="outline">
                <Link to={`/clients/${client.id}/edit`}>
                  <Edit3 className="size-4" /> Edit
                </Link>
              </Button>
              <Button asChild>
                <Link to={`/invoices/new?clientId=${client.id}`}>
                  <Plus className="size-4" /> New invoice
                </Link>
              </Button>
            </>
          ) : null
        }
      />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_320px]">
        <div className="flex flex-col gap-6">
          {canEditOrg ? (
            <Card>
              <CardContent className="pt-6">
                <ImageUploader
                  title="Photo"
                  hint="Used to identify the client across invoices and lists. PNG or JPG up to 5 MB."
                  preview={
                    <ClientAvatar
                      name={client.name}
                      photoUrl={client.photoUrl ?? null}
                      tag={inferClientTag({ name: client.name, company: client.company })}
                      size={72}
                      radius={16}
                      fontSize={22}
                    />
                  }
                  hasImage={Boolean(client.photoUrl)}
                  loading={uploadingPhoto}
                  onUpload={onUploadPhoto}
                />
              </CardContent>
            </Card>
          ) : null}

          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Contact</CardTitle>
              {!client.isActive ? <Badge variant="outline">Archived</Badge> : null}
            </CardHeader>
            <CardContent className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
              <Detail label="Email">
                {email ? (
                  <a className="hover:underline" href={`mailto:${email}`}>
                    <Mail className="mr-1.5 inline size-3.5 align-text-bottom" />
                    {email}
                  </a>
                ) : (
                  <Empty />
                )}
              </Detail>
              <Detail label="Phone">
                {phone ? (
                  <span>
                    <Phone className="mr-1.5 inline size-3.5 align-text-bottom" />
                    {phone}
                  </span>
                ) : (
                  <Empty />
                )}
              </Detail>
              <Detail label="Website">
                {website ? (
                  <a className="hover:underline" href={website} target="_blank" rel="noreferrer">
                    {website}
                  </a>
                ) : (
                  <Empty />
                )}
              </Detail>
              <Detail label="Tax ID">{client.taxId || <Empty />}</Detail>
            </CardContent>
          </Card>

          {client.notes ? (
            <Card>
              <CardHeader>
                <CardTitle>Notes</CardTitle>
              </CardHeader>
              <CardContent className="whitespace-pre-line text-sm leading-relaxed text-muted-foreground">
                {client.notes}
              </CardContent>
            </Card>
          ) : null}

          <Card>
            <CardHeader>
              <CardTitle>Invoices</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-3">
              {clientInvoices.length === 0 ? (
                <p className="text-sm text-muted-foreground">No invoices yet for this client.</p>
              ) : (
                clientInvoices.map((inv) => (
                  <Link
                    key={inv.id}
                    to={`/invoices/${inv.id}`}
                    className="flex items-center justify-between rounded-md border border-line bg-secondary/30 px-4 py-3 text-sm transition-colors hover:bg-secondary/60"
                  >
                    <div>
                      <p className="font-medium text-foreground">{inv.invoiceNumber}</p>
                      <p className="text-xs text-muted-foreground">
                        Due {new Date(inv.dueDate).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge variant={statusVariant(inv.status)}>{inv.status}</Badge>
                      <span className="font-mono text-sm font-medium text-foreground">
                        {displayWireMoney(inv.totals.total, inv.currency)}
                      </span>
                    </div>
                  </Link>
                ))
              )}
            </CardContent>
          </Card>
        </div>

        <aside className="flex flex-col gap-4">
          <Card>
            <CardHeader>
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                Outstanding
              </p>
              <CardTitle className="font-display text-3xl font-semibold tracking-tight">
                {client.isActive
                  ? displayWireMoney(outstanding.toString(), clientInvoices[0]?.currency ?? 'USD')
                  : '—'}
              </CardTitle>
            </CardHeader>
          </Card>
          {canEditOrg ? (
            <Button
              variant="outline"
              className="text-destructive"
              onClick={() => setConfirmDelete(true)}
            >
              <Trash2 className="size-4" /> Delete client
            </Button>
          ) : null}
        </aside>
      </div>

      <Dialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete {client.name}?</DialogTitle>
            <DialogDescription>
              This permanently removes the client. Their existing invoices stay
              put — you just won't be able to bill them again.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmDelete(false)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              loading={deleting}
              onClick={async () => {
                try {
                  await deleteClient(client.id);
                  toast({ variant: 'success', title: 'Client deleted' });
                  navigate('/clients', { replace: true });
                } catch (err) {
                  const copy = describeAppError(toAppError(err));
                  toast({
                    variant: 'danger',
                    title: copy.title,
                    description: copy.message,
                  });
                }
              }}
            >
              Delete client
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Page>
  );
}

function Detail({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
        {label}
      </p>
      <p className="mt-1 text-sm text-foreground">{children}</p>
    </div>
  );
}

function Empty() {
  return <span className="text-muted-foreground">—</span>;
}

function statusVariant(
  status: string,
): 'success' | 'warning' | 'danger' | 'default' {
  switch (status) {
    case 'paid':
      return 'success';
    case 'overdue':
      return 'danger';
    case 'sent':
    case 'pending':
      return 'warning';
    default:
      return 'default';
  }
}
